/** Каркас боковой карточки сущности (drill-through): справа поверх страницы — подложка, шапка
 *  («Назад» к предыдущей карточке, подпись вида, ✕), своя прокрутка, фокус и Esc
 *  (`useDialogFocus`), страница под панелью не прокручивается, ошибка рендера содержимого — текстом
 *  внутри панели. Содержимое — у раздела: «Аналитика МП» (`pages/ozon-analytics/EntityPanel.tsx`),
 *  «Аналитика ОПТ» (`pages/wholesale/CardPanel.tsx`). */
import { ArrowLeft, X } from "lucide-react";
import React, { Component, useEffect, useId, useRef } from "react";

import { useDialogFocus } from "@/lib/useDialogFocus";
import { cn } from "@/lib/utils";

/** Исключение в рендере карточки не роняет страницу: текст ошибки — внутри панели. */
export class CardErrorBoundary extends Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("Карточка сущности: ошибка рендера", error, info.componentStack);
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div
          role="alert"
          data-testid="card-render-error"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
        >
          Ошибка отображения карточки: {this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}

export function EntityPanelFrame({
  label,
  cardId,
  onClose,
  onBack,
  widthClass = "w-[min(760px,100vw)]",
  children,
}: {
  /** подпись вида карточки в шапке («Карточка артикула») */
  label: string;
  /** сущность карточки: смена — фокус на ✕ и свежее состояние содержимого */
  cardId: string;
  onClose: () => void;
  /** «Назад» к предыдущей карточке (открыли из карточки); нет — кнопки нет */
  onBack?: () => void;
  widthClass?: string;
  /** `titleId` — id заголовка содержимого (aria-labelledby панели) */
  children: (titleId: string) => React.ReactNode;
}): React.ReactElement {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useDialogFocus(panelRef, closeRef, onClose);
  // смена карточки внутри панели: кнопка, по которой перешли, исчезла — фокус на ✕
  useEffect(() => {
    closeRef.current?.focus();
  }, [cardId]);
  // страница под панелью не прокручивается; прежнее значение — на место при закрытии
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        data-testid="entity-panel-backdrop"
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="entity-panel"
        className={cn(
          "absolute inset-y-0 right-0 flex flex-col border-l bg-background text-foreground shadow-xl duration-150 animate-in slide-in-from-right",
          widthClass,
        )}
      >
        <div className="flex items-center gap-2 border-b px-4 py-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Назад к предыдущей карточке"
              title="Назад к предыдущей карточке"
              className="rounded p-1 hover:bg-accent"
            >
              <ArrowLeft className="size-4" aria-hidden />
            </button>
          )}
          <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            aria-label="Закрыть карточку"
            className="ml-auto rounded p-1 hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain p-4">
          {/* key: другая сущность — свежее состояние карточки (шаг графика и т.п.) */}
          <CardErrorBoundary key={cardId}>{children(titleId)}</CardErrorBoundary>
        </div>
      </div>
    </div>
  );
}
